import axios from 'axios';

// The application's legacy Axios response interceptor returns HTTP errors as
// fulfilled values. Bulk parsing is a durable workflow, so accepting one of
// those values as a successful empty response can make the UI stop updating
// after a reload. Keep the defensive check local to this feature until the
// shared interceptor can be migrated without changing unrelated screens.
const responseData = (response) => {
  if (response?.isAxiosError || response instanceof Error) throw response;
  if (!response || Number(response?.status) >= 400) {
    const error = new Error(
      response?.data?.message ||
        response?.data?.detail ||
        'The bulk CV API did not return a successful response.'
    );
    error.response = response || null;
    error.code = response?.data?.error?.code || response?.data?.code;
    throw error;
  }
  return response.data;
};

const REST_STATE_TIMEOUT_MS = 20000;
const START_REQUEST_TIMEOUT_MS = 20000;
const stateRequestConfig = (config = {}) => ({
  timeout: REST_STATE_TIMEOUT_MS,
  ...config
});

const requiredReference = (value, label = 'batch') => {
  const reference = String(value ?? '').trim();
  if (
    !reference ||
    ['null', 'undefined', 'false', '[object object]'].includes(
      reference.toLowerCase()
    )
  ) {
    const error = new Error(`A valid bulk CV ${label} ID is required.`);
    error.code = `invalid_${label}_id`;
    throw error;
  }
  return reference;
};

// Keep every batch endpoint in one place. The legacy endpoints stay available
// during the staged rollout, but new UI state is always reconciled from REST.
export const BULK_CV_ENDPOINTS = Object.freeze({
  batches: '/customer/bulk-cv/batches/',
  batch: (batchId) => `/customer/bulk-cv/batches/${batchId}/`,
  files: (batchId) => `/customer/bulk-cv/batches/${batchId}/files/`,
  start: (batchId) => `/customer/bulk-cv/batches/${batchId}/start/`,
  items: (batchId) => `/customer/bulk-cv/batches/${batchId}/items/`,
  resolveItem: (itemId) => `/customer/bulk-cv/items/${itemId}/resolve/`,
  bulkResolve: (batchId) =>
    `/customer/bulk-cv/batches/${batchId}/bulk-resolve/`,
  cancel: (batchId) => `/customer/bulk-cv/batches/${batchId}/cancel/`,
  dismiss: (batchId) => `/customer/bulk-cv/batches/${batchId}/dismiss/`,
  settings: '/customer/bulk-cv/settings/',
  reports: '/customer/bulk-cv/reports/',
  legacyUpload: '/customer/upload-bulk-file/',
  legacyResolve: '/customer/bulk-resume/update-duplicate/',
  legacyComplete: '/customer/parsing-mark-as-completed/',
  legacyCancel: '/customer/cancel-parsing/'
});

export const BULK_CV_FILE_POLICY = Object.freeze({
  maxFiles: 50,
  maxSizeBytes: 20 * 1024 * 1024,
  uploadChunkSize: 5,
  extensions: ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg'],
  mimeTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/png',
    'image/jpeg'
  ]
});

export const getCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.batches)) return payload.batches;
  return [];
};

const getBatchPayload = (payload) => payload?.batch || payload;

export const isBatchApiUnavailable = (error) =>
  [404, 405, 501].includes(error?.response?.status);

export const listBatches = async (params = {}) => {
  const response = await axios.get(
    BULK_CV_ENDPOINTS.batches,
    stateRequestConfig({ params })
  );
  return responseData(response);
};

export const getBulkCvSettings = async () => {
  const response = await axios.get(
    BULK_CV_ENDPOINTS.settings,
    stateRequestConfig()
  );
  return responseData(response);
};

export const getBulkCvReports = async (params = {}) => {
  const response = await axios.get(
    BULK_CV_ENDPOINTS.reports,
    stateRequestConfig({ params })
  );
  return responseData(response);
};

export const createBatch = async (payload) => {
  const response = await axios.post(BULK_CV_ENDPOINTS.batches, payload);
  return getBatchPayload(responseData(response));
};

export const getBatch = async (batchId) => {
  const reference = requiredReference(batchId);
  const response = await axios.get(
    BULK_CV_ENDPOINTS.batch(reference),
    stateRequestConfig()
  );
  return getBatchPayload(responseData(response));
};

export const getBatchItems = async (batchId, params = {}) => {
  const reference = requiredReference(batchId);
  const response = await axios.get(BULK_CV_ENDPOINTS.items(reference), {
    ...stateRequestConfig(),
    params
  });
  return responseData(response);
};

export const uploadBatchFiles = async (batchId, files, clientFiles) => {
  const reference = requiredReference(batchId);
  const formData = new FormData();
  files.forEach((file) => formData.append('files[]', file));

  const metadata = clientFiles.map((file) => ({
    client_file_id: file.client_file_id,
    file_id: file.client_file_id,
    name: file.name,
    size: file.size_bytes
  }));

  // `metadata` is the new contract. `data` lets a rolling-deployment backend
  // accept the same request without making the browser upload each file twice.
  formData.append('metadata', JSON.stringify(metadata));
  formData.append('data', JSON.stringify({ file_ids: metadata }));

  const response = await axios.post(
    BULK_CV_ENDPOINTS.files(reference),
    formData
  );
  return responseData(response);
};

export const startBatch = async (batchId) => {
  const reference = requiredReference(batchId);
  // Starting is a short state transition, not a parser request. Bound the
  // browser wait so a lost response cannot leave the upload UI spinning
  // forever; the caller then reads the batch back from REST to learn whether
  // the server committed the idempotent transition.
  const response = await axios.post(
    BULK_CV_ENDPOINTS.start(reference),
    {},
    {
      timeout: START_REQUEST_TIMEOUT_MS
    }
  );
  return getBatchPayload(responseData(response));
};

export const resolveBatchItem = async (itemId, payload) => {
  const response = await axios.post(
    BULK_CV_ENDPOINTS.resolveItem(itemId),
    payload
  );
  return responseData(response);
};

export const resolveBatchItems = async (batchId, payload) => {
  const reference = requiredReference(batchId);
  const response = await axios.post(
    BULK_CV_ENDPOINTS.bulkResolve(reference),
    payload
  );
  return responseData(response);
};

export const cancelBatch = async (batchId) => {
  const reference = requiredReference(batchId);
  const response = await axios.post(BULK_CV_ENDPOINTS.cancel(reference), {});
  return responseData(response);
};

export const dismissBatch = async (batchId) => {
  const reference = requiredReference(batchId);
  const response = await axios.post(BULK_CV_ENDPOINTS.dismiss(reference), {});
  return responseData(response);
};

export const getLegacyItems = async () => {
  const response = await axios.get(
    BULK_CV_ENDPOINTS.legacyUpload,
    stateRequestConfig()
  );
  return responseData(response);
};

export const resolveLegacyItem = async (payload) => {
  const response = await axios.post(BULK_CV_ENDPOINTS.legacyResolve, payload);
  return responseData(response);
};

export const completeLegacyBatch = async () => {
  const response = await axios.post(BULK_CV_ENDPOINTS.legacyComplete, {});
  return responseData(response);
};

export const cancelLegacyBatch = async () => {
  const response = await axios.post(BULK_CV_ENDPOINTS.legacyCancel, {});
  return responseData(response);
};
